import { useEffect, useMemo } from 'react';
import {
    __,
    addIndex,
    defaultTo,
    filter,
    find,
    includes,
    insert,
    map,
    pick,
    pipe,
    prop,
    propEq,
    when
} from 'ramda';
import { getCookie } from 'js/utils/cookie.util';
import { isProfileAllowed } from 'js/utils/segmentation/segmentation.util';
import { useAppSelector, useDeviceSelector, useUserSelector, useGameConfigurationSelector } from 'js/store/hooks';
import { usePortletConfig, usePortletDispatch, usePortletSelector } from 'js/components/system/Portlet';

import * as actions from './actions';
import * as helpers from './helpers';
import { CATEGORY_TYPE } from './constants';
import { filterGamesByPGG2, parseDynamicCategories } from './BICategories';

export const getCookieFeedName = name => getCookie(`_category_feed_${name}`);

export const getProfileFeedName = templates => pipe(
    find(profileFeed => isProfileAllowed(profileFeed.urltemplates, templates)),
    prop('name')
);

export const getFeedName = ({
    gamesFeeds,
    urlTemplates,
    categoriesFeedName = '',
    categoriesCookieName = '',
    enableUserProfile = false,
    profileFeeds = []
}) => {
    const feedsNames = map(prop('name'), gamesFeeds);
    const possibleFeedsNames = [
        getCookieFeedName(categoriesCookieName),
        enableUserProfile ? getProfileFeedName(urlTemplates)(profileFeeds) : null,
        categoriesFeedName
    ];

    return find(includes(__, feedsNames), possibleFeedsNames);
};

export const getFeedByName = name => pipe(
    find(propEq('name', name)),
    defaultTo({ categories: [] })
);

export const useFeed = () => {
    const isGamesLoaded = useGameConfigurationSelector(prop('isGamesLoaded'));
    const { isOldGamesFeedsLoaded, oldGamesFeeds } = useAppSelector(pick(['isOldGamesFeedsLoaded', 'oldGamesFeeds']));
    const urlTemplates = useUserSelector(prop('urlTemplates'));
    const { categoriesFeedName, categoriesCookieName, enableUserProfile, profileFeeds } = usePortletConfig();
    const dispatch = usePortletDispatch();

    const feedName = useMemo(
        () => getFeedName({
            gamesFeeds: oldGamesFeeds,
            urlTemplates,
            categoriesFeedName,
            categoriesCookieName,
            enableUserProfile,
            profileFeeds
        }),
        [
            oldGamesFeeds,
            urlTemplates,
            categoriesFeedName,
            categoriesCookieName,
            enableUserProfile,
            profileFeeds
        ]
    );

    const feed = useMemo(
        () => getFeedByName(feedName)(oldGamesFeeds),
        [feedName, oldGamesFeeds]
    );

    useEffect(() => {
        isGamesLoaded && isOldGamesFeedsLoaded && dispatch(actions.setFeedAction({ feedName, feed }));
    }, [feedName, feed, dispatch, isGamesLoaded, isOldGamesFeedsLoaded]);
};

export const filterGames = existingGames => map(category => ({
    ...category,
    gameCodes: category.gameCodes.filter(code => code in existingGames)
}));

export const filterGamesExcludedFromPgg2 = existingGames => map(category => ({
    ...category,
    gameCodes: category.gameCodes.filter((code) => {
        const { isExcludedFromPGG } = existingGames[code];
        const { enabled } = category.pgg2;

        return !enabled || !isExcludedFromPGG;
    })
}));

export const filterEmptyCategories = filter(category => category.gameCodes.length);

export const addType = (numberOfPinnedCategories, isDesktop) => addIndex(map)((category, index) => ({
    ...category,
    type: index < numberOfPinnedCategories
        ? CATEGORY_TYPE.PINNED
        : isDesktop
            ? null
            : CATEGORY_TYPE.TAB
}));

export const addFavoritesCategory = ({
    isDesktop,
    games,
    favoriteGames,
    favoritesLocation,
    numberOfPinnedCategories
}) => (categories) => {
    const gameCodes = favoriteGames.filter(code => code in games);
    const isTab = favoritesLocation === 'round';

    return gameCodes.length
        ? insert(numberOfPinnedCategories, {
            gameCodes,
            name: 'favorites',
            cid: CATEGORY_TYPE.FAVORITES,
            type: isTab ? CATEGORY_TYPE.TAB : CATEGORY_TYPE.FAVORITES,
            roundIcon: 'favorite',
            withBounceInAnimation: isDesktop ? true : !isTab,
            displayName: 'playtech.mobile.responsivegames.category.favorites',
            displayNameShort: 'playtech.mobile.responsivegames.category.favorites.short'
        }, categories)
        : categories;
};

export const addRecentlyPlayedCategory = ({
    games,
    recentlyPlayedGames,
    numberOfPinnedCategories
}) => (categories) => {
    const gameCodes = recentlyPlayedGames.filter(code => code in games);

    return gameCodes.length
        ? insert(numberOfPinnedCategories, {
            gameCodes,
            name: 'recentlyplayed',
            cid: CATEGORY_TYPE.RECENTLY,
            type: CATEGORY_TYPE.RECENTLY,
            roundIcon: 'youplayed',
            withBounceInAnimation: true,
            displayName: 'playtech.mobile.responsivegames.category.recentlyplayed',
            displayNameShort: 'playtech.mobile.responsivegames.category.recentlyplayed.short'
        }, categories)
        : categories;
};

export const getCategories = ({
    games,
    favoriteGames = [],
    recentlyPlayedGames = [],
    isLoggedIn,
    isDesktop,
    dynamicCategories,
    numberOfPinnedCategories,
    includeRecentlyPlayed,
    includeFavoritesCategory,
    showFavorites,
    favoritesLocation,
    feedName,
    feed: { categories = [] }
}) => pipe(
    parseDynamicCategories(dynamicCategories, feedName),
    filterGames(games),
    filterGamesExcludedFromPgg2(games),
    filterEmptyCategories,
    filterGamesByPGG2,
    addType(numberOfPinnedCategories, isDesktop),
    when(
        () => isLoggedIn && includeFavoritesCategory && showFavorites && games,
        addFavoritesCategory({
            isDesktop,
            games,
            favoriteGames,
            favoritesLocation,
            numberOfPinnedCategories
        })
    ),
    when(
        () => isLoggedIn && includeRecentlyPlayed && games,
        addRecentlyPlayedCategory({
            games,
            recentlyPlayedGames,
            numberOfPinnedCategories
        })
    ),
    helpers.addLiveLaunchAliasesToCategory(games)
)(categories);

export const useCategories = () => {
    const { games, favoriteGames } = useGameConfigurationSelector(pick(['games', 'favoriteGames']));
    const { dynamicCategories, recentlyPlayedGames, feedName, feed, liveLaunchAliases } = usePortletSelector(pick([
        'dynamicCategories', 'recentlyPlayedGames', 'feedName', 'feed', 'liveLaunchAliases'
    ]));
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const {
        numberOfPinnedCategories,
        includeRecentlyPlayed,
        includeFavoritesCategory,
        showFavorites,
        favoritesLocation
    } = usePortletConfig();
    const dispatch = usePortletDispatch();

    const categories = useMemo(
        () => getCategories({
            games,
            favoriteGames,
            recentlyPlayedGames,
            isLoggedIn,
            isDesktop,
            dynamicCategories,
            numberOfPinnedCategories,
            includeRecentlyPlayed,
            includeFavoritesCategory,
            showFavorites,
            favoritesLocation,
            feedName,
            feed
        }),
        [
            games,
            favoriteGames,
            recentlyPlayedGames,
            isLoggedIn,
            isDesktop,
            dynamicCategories,
            numberOfPinnedCategories,
            includeRecentlyPlayed,
            includeFavoritesCategory,
            showFavorites,
            favoritesLocation,
            feedName,
            feed
        ]);

    const tableAliases = useMemo(() => [...new Set(categories.reduce((aliases, category) => [
        ...aliases,
        ...category.liveLaunchAliases
    ], []))], [categories]);

    useEffect(() => {
        const newTableAliases = tableAliases.filter(alias => !liveLaunchAliases.includes(alias));

        if (newTableAliases.length) {
            dispatch(actions.updateLiveLaunchAliases(tableAliases));
        }
    }, [dispatch, tableAliases, liveLaunchAliases]);

    useEffect(() => {
        if (!categories.length && dynamicCategories.length) {
            dispatch(actions.dynamicCategoriesFetchedAction());
        } else {
            dispatch(actions.setCategoriesAction(categories));
        }

    }, [categories, dynamicCategories, dispatch, games]);
};
